import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Use the default axios instance instead of creating a new one
// This allows it to benefit from interceptors set up by AuthContext
const apiClient: AxiosInstance = axios;

// Set default base URL for all requests
if (API_BASE_URL) {
  apiClient.defaults.baseURL = API_BASE_URL;
}

// Set default timeout
apiClient.defaults.timeout = 30000; // 30 seconds

// Set default headers
apiClient.defaults.headers.common['Content-Type'] = 'application/json';

// Request interceptor to handle expected failures gracefully
apiClient.interceptors.request.use(
  (config) => {
    // For specific endpoints, don't treat 404/403 as errors
    const url = config.url || '';
    if (url.includes('/api/trades/ious/my-balance')) {
      // Don't treat 404 as error for IOU balance (endpoint not implemented)
      config.validateStatus = (status) => status < 500; // Accept 404 as valid
    } else if (url.includes('/api/users') && !url.match(/\/api\/users\/\d+/)) {
      // Don't treat 403 as error for users list (expected for non-admin)
      config.validateStatus = (status) => status < 500; // Accept 403 as valid
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Handle expected "errors" as successful responses
    const url = response.config?.url || '';
    const status = response.status;
    
    // For IOU balance 404, return a default response structure
    if (status === 404 && url.includes('/api/trades/ious/my-balance')) {
      // Transform 404 into a successful response with default data
      return {
        ...response,
        data: {
          iousOwed: [],
          iousOwedToMe: [],
          balance: { owed: 0, owedToMe: 0, net: 0 },
          limits: { maxOutstanding: 3, maxDebt: 2 }
        },
        status: 200,
        statusText: 'OK'
      };
    }
    
    // For users list 403, return empty array
    if (status === 403 && url.includes('/api/users') && !url.match(/\/api\/users\/\d+/)) {
      return {
        ...response,
        data: [],
        status: 200,
        statusText: 'OK'
      };
    }
    
    return response;
  },
  (error) => {
    // Handle common error scenarios
    if (error.response) {
      const { status } = error.response;
      const errorUrl = error.config?.url || '';
      
      // For expected errors that we want to handle silently, convert to success response
      if (status === 403 && errorUrl.includes('/api/users') && !errorUrl.match(/\/api\/users\/\d+/)) {
        // Expected 403 for non-admin users - return empty array
        return Promise.resolve({
          ...error.response,
          config: error.config,
          data: [],
          status: 200,
          statusText: 'OK',
          headers: error.response.headers
        });
      }
      
      // For IOU balance 404, return default data
      if (status === 404 && errorUrl.includes('/api/trades/ious/my-balance')) {
        return Promise.resolve({
          ...error.response,
          config: error.config,
          data: {
            iousOwed: [],
            iousOwedToMe: [],
            balance: { owed: 0, owedToMe: 0, net: 0 },
            limits: { maxOutstanding: 3, maxDebt: 2 }
          },
          status: 200,
          statusText: 'OK',
          headers: error.response.headers
        });
      }
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          localStorage.removeItem('txjet_user');
          window.location.href = '/login';
          break;
        case 403:
          // Forbidden - user doesn't have permission (let it through for UI to handle)
          break;
        case 404:
          // Not found
          break;
        case 422:
          // Validation error
          break;
        case 500:
          // Server error
          break;
        default:
          // Handle other HTTP errors
      }
    } else if (error.request) {
      // Network error
    } else {
      // Other error
    }
    
    return Promise.reject(error);
  }
);

// Generic API methods with type safety
export const api = {
  // GET request
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.get<ApiResponse<T>>(url, config);
    return response.data;
  },

  // POST request
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  // PUT request
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  // DELETE request
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete<ApiResponse<T>>(url, config);
    return response.data;
  },

  // PATCH request
  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  },
};

export default apiClient;
